// src/pages/Vouchers.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';

function Vouchers() {
  const [vouchers, setVouchers] = useState([]);
  const [filteredVouchers, setFilteredVouchers] = useState([]);
  const [stats, setStats] = useState({
    total_vouchers: 0,
    used_vouchers: 0,
    unused_vouchers: 0,
    total_amount: 0,
    used_amount: 0
  });
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newVoucher, setNewVoucher] = useState({
    amount: '',
    expiry_days: 30,
    description: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const API_URL = 'https://boda-backend-wuea.onrender.com/api/admin';

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterVouchers();
  }, [searchTerm, vouchers]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [vouchersRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/vouchers`),
        axios.get(`${API_URL}/vouchers/stats`)
      ]);
      setVouchers(vouchersRes.data.vouchers || []);
      setFilteredVouchers(vouchersRes.data.vouchers || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching vouchers:', error);
      setError('Failed to load vouchers');
    } finally {
      setLoading(false);
    }
  };

  const filterVouchers = () => {
    if (!searchTerm.trim()) {
      setFilteredVouchers(vouchers);
      return;
    }
    const term = searchTerm.toLowerCase().trim();
    const filtered = vouchers.filter(v => {
      const code = (v.code || '').toLowerCase();
      const description = (v.description || '').toLowerCase();
      const usedByName = (v.used_by_name || '').toLowerCase();
      const amount = String(v.amount || '');
      const status = v.is_used ? 'used' : 'active';
      
      return code.includes(term) || 
             description.includes(term) || 
             usedByName.includes(term) || 
             amount.includes(term) ||
             status.includes(term);
    });
    setFilteredVouchers(filtered);
  };

  const createVoucher = async () => {
    if (!newVoucher.amount || parseFloat(newVoucher.amount) <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    try {
      const response = await axios.post(`${API_URL}/vouchers`, {
        amount: parseFloat(newVoucher.amount),
        expiry_days: parseInt(newVoucher.expiry_days),
        description: newVoucher.description
      });
      
      if (response.data.success) {
        setSuccess(`Voucher created: ${response.data.voucher.code}`);
        setShowCreateModal(false);
        setNewVoucher({ amount: '', expiry_days: 30, description: '' });
        fetchData();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      setError('Failed to create voucher');
      setTimeout(() => setError(''), 3000);
    }
  };

  const deleteVoucher = async (id, code) => {
    if (!window.confirm(`Delete voucher ${code}? Only unused vouchers can be deleted.`)) return;
    
    try {
      await axios.delete(`${API_URL}/vouchers/${id}`);
      setSuccess('Voucher deleted');
      fetchData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete voucher');
      setTimeout(() => setError(''), 3000);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  // Search stats
  const searchStats = {
    total: vouchers.length,
    shown: filteredVouchers.length,
    hidden: vouchers.length - filteredVouchers.length
  };

  const StatCard = ({ title, value, icon, color }) => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      flex: 1,
      minWidth: '150px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>{title}</p>
          <p style={{ fontSize: '24px', fontWeight: 'bold', margin: '10px 0 0 0' }}>{value}</p>
        </div>
        <div style={{
          backgroundColor: color,
          borderRadius: '50%',
          width: '48px',
          height: '48px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <span style={{ fontSize: '24px' }}>{icon}</span>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '40px', marginBottom: '16px' }}>🔄</div>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Loading vouchers...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 20px 0' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>Voucher Management</h1>
          <div style={{ display: 'flex', gap: '16px', marginTop: '4px', fontSize: '14px', color: '#64748b' }}>
            <span>🎫 {vouchers.length} total</span>
            <span style={{ color: '#10b981' }}>✅ {stats.used_vouchers} used</span>
            <span style={{ color: '#f59e0b' }}>🆕 {stats.unused_vouchers} unused</span>
            {searchTerm && (
              <span style={{ color: '#f97316' }}>
                🔍 {filteredVouchers.length} shown
                {searchStats.hidden > 0 && ` (${searchStats.hidden} hidden)`}
              </span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="🔍 Search vouchers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '10px 16px',
                paddingRight: searchTerm ? '40px' : '16px',
                borderRadius: '10px',
                border: '2px solid #e2e8f0',
                width: '220px',
                fontSize: '14px',
                outline: 'none',
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '16px',
                  color: '#94a3b8',
                }}
              >
                ✕
              </button>
            )}
          </div>
          <button
            onClick={fetchData}
            style={{
              padding: '10px 20px',
              backgroundColor: '#f97316',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            🔄 Refresh
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              padding: '10px 20px',
              backgroundColor: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '10px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            + Create Voucher
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '16px',
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          backgroundColor: '#dcfce7',
          color: '#166534',
          padding: '12px',
          borderRadius: '8px',
          marginBottom: '16px',
        }}>
          {success}
        </div>
      )}

      {/* Stats Cards */}
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '24px' }}>
        <StatCard title="Total Vouchers" value={stats.total_vouchers} icon="🎫" color="#e0f2fe" />
        <StatCard title="Used" value={stats.used_vouchers} icon="✅" color="#dcfce7" />
        <StatCard title="Unused" value={stats.unused_vouchers} icon="🆕" color="#fef3c7" />
        <StatCard title="Total Amount" value={`UGX ${stats.total_amount.toLocaleString()}`} icon="💰" color="#fce7f3" />
        <StatCard title="Used Amount" value={`UGX ${stats.used_amount.toLocaleString()}`} icon="💵" color="#e0e7ff" />
      </div>

      {/* Vouchers Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        overflow: 'auto',
      }}>
        <h3 style={{ marginBottom: '20px' }}>All Vouchers</h3>
        
        {filteredVouchers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔍</div>
            <p style={{ color: '#64748b', fontSize: '16px' }}>
              {searchTerm ? `No vouchers found matching "${searchTerm}"` : 'No vouchers yet. Create your first voucher!'}
            </p>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#f97316',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  marginTop: '8px',
                }}
              >
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Code</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Amount</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Status</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Used By</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Used At</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Expires</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Created</th>
                <th style={{ textAlign: 'left', padding: '12px', fontWeight: '600' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredVouchers.map((voucher) => (
                <tr key={voucher.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px', fontFamily: 'monospace', fontWeight: 'bold', color: '#f97316' }}>
                    {voucher.code}
                  </td>
                  <td style={{ padding: '12px', color: '#16a34a', fontWeight: 'bold' }}>
                    UGX {voucher.amount?.toLocaleString()}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <span style={{
                      padding: '4px 8px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      backgroundColor: voucher.is_used ? '#dcfce7' : '#fef3c7',
                      color: voucher.is_used ? '#166534' : '#92400e',
                    }}>
                      {voucher.is_used ? 'Used' : 'Active'}
                    </span>
                  </td>
                  <td style={{ padding: '12px' }}>{voucher.used_by_name || '—'}</td>
                  <td style={{ padding: '12px', fontSize: '13px', color: '#64748b' }}>{formatDate(voucher.used_at)}</td>
                  <td style={{ padding: '12px', color: new Date(voucher.expires_at) < new Date() ? '#dc2626' : '#64748b' }}>
                    {formatDate(voucher.expires_at)}
                  </td>
                  <td style={{ padding: '12px', fontSize: '13px', color: '#64748b' }}>{formatDate(voucher.created_at)}</td>
                  <td style={{ padding: '12px' }}>
                    {!voucher.is_used && (
                      <button
                        onClick={() => deleteVoucher(voucher.id, voucher.code)}
                        style={{
                          padding: '4px 12px',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        
        {filteredVouchers.length > 0 && (
          <div style={{ 
            padding: '10px', 
            color: '#64748b', 
            fontSize: '12px',
            borderTop: '1px solid #e2e8f0',
            marginTop: '12px',
          }}>
            Showing {filteredVouchers.length} of {vouchers.length} vouchers
          </div>
        )}
      </div>

      {/* Create Voucher Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '24px',
            width: '90%',
            maxWidth: '450px',
          }}>
            <h2 style={{ marginBottom: '20px' }}>Create New Voucher</h2>
            
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Amount (UGX)</label>
              <input
                type="number"
                value={newVoucher.amount}
                onChange={(e) => setNewVoucher({ ...newVoucher, amount: e.target.value })}
                placeholder="e.g., 5000, 10000, 20000"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '16px',
                }}
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Valid Days</label>
              <input
                type="number"
                value={newVoucher.expiry_days}
                onChange={(e) => setNewVoucher({ ...newVoucher, expiry_days: e.target.value })}
                placeholder="Days until expiry"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '16px',
                }}
              />
              <p style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>Default: 30 days</p>
            </div>

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Description (Optional)</label>
              <textarea
                value={newVoucher.description}
                onChange={(e) => setNewVoucher({ ...newVoucher, description: e.target.value })}
                placeholder="e.g., Welcome bonus, Promotion, Referral reward"
                rows="2"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowCreateModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#e2e8f0',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={createVoucher}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#f97316',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Create Voucher
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Vouchers;